"use client"

import { useState, useEffect, useRef, useMemo, useCallback } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useVirtualizer } from "@tanstack/react-virtual"
import { debounce } from "lodash"
import socket from "../socket/socket"
import DashboardLayout from "../layouts/DashboardLayout"
import {
  getAllLiveUserListAction,
  getOpenChatListAction,
  sendMessageOpenAction,
  uploadImageAction,
} from "../../services/action/common"
import { formatMessageTime, truncateString } from "../utils/functions/functions"
import { Globe, SmartphoneIcon as Android, Apple, FileText, Download } from "lucide-react"
import ChatArea from "./components/ChatArea"
import { useLocation } from "react-router-dom"

const LiveChatInternal = ({ userId, setopenLiveChat, isContentOnly }) => {
  const { openChatList } = useSelector((state) => state.commonReducer)
  const [message, setMessage] = useState("")
  const [activeVisitor, setActiveVisitor] = useState(0)
  const [showChat, setShowChat] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [activeFilter, setActiveFilter] = useState("Total")
  const [highlightedMessage, setHighlightedMessage] = useState(null)
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const [showOptions, setShowOptions] = useState(false)
  const [replyingTo, setReplyingTo] = useState(null)
  const [showScrollButton, setShowScrollButton] = useState(false)
  const messageRefs = useRef({})
  const parentRef = useRef(null)
  const chatContainerRef = useRef(null)
  const { allLiveUser, openUserChatList } = useSelector((state) => state.commonReducer)
  const { user } = useSelector((state) => state.authenticationReducer)
  const dispatch = useDispatch()

  const audioRef = useRef(null)

  // Pagination state
  const [pageData, setPageData] = useState({ page: 1, limit: 17 })
  const [hasMore, setHasMore] = useState(true)
  const [searchQuery, setSearchQuery] = useState("")
  const [unreadMessage, setUnreadMessage] = useState([])

  const [messages, setMessages] = useState({ chatList: [] })
  const [attachments, setAttachments] = useState([])
  // Add a new state for tracking file uploads in progress
  const [isUploading, setIsUploading] = useState(false)

  // Add a new state for filter panel visibility after the other state declarations (around line 50)
  const [showFilterPanel, setShowFilterPanel] = useState(false)

  // Add state for date range
  const [dateRange, setDateRange] = useState({ from: "", to: "" })

  // Add a new state for the filter modal
  const [showFilterModal, setShowFilterModal] = useState(false)

  // Add a state for country filter
  const [selectedCountry, setSelectedCountry] = useState("all")

  // Add a new state for the detailed view modal
  const [showDetailedView, setShowDetailedView] = useState(false)

  const fetchMoreData = useCallback(() => {
    // Add a flag to prevent duplicate calls
    if (fetchMoreData.isLoading) return

    fetchMoreData.isLoading = true

    getAllLiveUser({ page: pageData?.page + 1 })
    setPageData((prev) => ({ ...prev, page: prev.page + 1 }))

    // Reset the flag after a short delay
    setTimeout(() => {
      fetchMoreData.isLoading = false
    }, 300)
  }, [pageData?.page, setPageData])

  // Initialize the loading flag
  fetchMoreData.isLoading = false

  // Set up virtualizer for visitor list
  const rowVirtualizer = useVirtualizer({
    count: allLiveUser?.data?.length || 0,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 72, // Estimated height of each visitor item
    overscan: 5,
  })

  // Scroll to bottom of chat
  const scrollToBottom = useCallback(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight
    }
  }, [])

  // Check if chat is scrolled up
  const handleChatScroll = useCallback(() => {
    if (chatContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current
      const isScrolledUp = scrollHeight - scrollTop - clientHeight > 100
      setShowScrollButton(isScrolledUp)
    }
  }, [])

  // Debounced function for loading more data
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFetchMoreData = useCallback(
    debounce(() => {
      // Don't fetch more data if we're currently searching
      if (searchQuery.trim() || showDetailedView) return

      if (hasMore && allLiveUser?.data?.length > 0) {
        fetchMoreData()
      }
    }, 300),
    [hasMore, allLiveUser?.data?.length, searchQuery, showDetailedView, fetchMoreData],
  )

  // Check if we need to load more data
  useEffect(() => {
    const [lastItem] = [...rowVirtualizer.getVirtualItems()].reverse()

    if (!lastItem) {
      return
    }

    // Don't load more data if we're currently searching
    if (window.isSearching) {
      return
    }

    if (lastItem.index >= (allLiveUser?.data?.length || 0) - 3 && hasMore && allLiveUser?.data?.length > 0) {
      debouncedFetchMoreData()
    }
  }, [rowVirtualizer.getVirtualItems(), allLiveUser?.data?.length, hasMore, debouncedFetchMoreData])

  // Check if mobile - memoized event handler
  useEffect(() => {
    const checkIfMobile = () => setIsMobile(window.innerWidth < 768)
    checkIfMobile()
    window.addEventListener("resize", checkIfMobile)
    return () => window.removeEventListener("resize", checkIfMobile)
  }, [])

  // Scroll to highlighted message
  useEffect(() => {
    if (highlightedMessage !== null && messageRefs.current[highlightedMessage]) {
      messageRefs.current[highlightedMessage]?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      })

      // Remove highlight after 2 seconds
      const timer = setTimeout(() => setHighlightedMessage(null), 2000)
      return () => clearTimeout(timer)
    }
  }, [highlightedMessage])

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (openChatList?.chatList?.length) {
      scrollToBottom()
    }
  }, [openChatList?.chatList?.length, scrollToBottom])

  // Add scroll event listener for chat container
  useEffect(() => {
    const chatContainer = chatContainerRef.current
    if (chatContainer) {
      chatContainer.addEventListener("scroll", handleChatScroll)
      return () => chatContainer.removeEventListener("scroll", handleChatScroll)
    }
  }, [handleChatScroll])

  // Country flags mapping - memoized to prevent recreation on each render
  const countryFlags = useMemo(
    () => ({
      GB: { name: "United Kingdom", flag: "https://flagcdn.com/w40/gb.png" },
      IN: { name: "India", flag: "https://flagcdn.com/w40/in.png" },
      US: { name: "United States", flag: "https://flagcdn.com/w40/us.png" },
      CA: { name: "Canada", flag: "https://flagcdn.com/w40/ca.png" },
      AU: { name: "Australia", flag: "https://flagcdn.com/w40/au.png" },
      JP: { name: "Japan", flag: "https://flagcdn.com/w40/jp.png" },
      DE: { name: "Germany", flag: "https://flagcdn.com/w40/de.png" },
      FR: { name: "France", flag: "https://flagcdn.com/w40/fr.png" },
      IT: { name: "Italy", flag: "https://flagcdn.com/w40/it.png" },
      ES: { name: "Spain", flag: "https://flagcdn.com/w40/es.png" },
      SG: { name: "Singapore", flag: "https://flagcdn.com/w40/sg.png" },
      CN: { name: "China", flag: "https://flagcdn.com/w40/cn.png" },
      // Add more countries as needed
    }),
    [],
  )

  // Stats data - memoized
  const stats = useMemo(
    () => [
      { label: "Total", value: allLiveUser?.paginations?.total || "0" },
      { label: "Online", value: allLiveUser?.paginations?.onlineUsers || "0" },
      {
        label: "Initiated",
        value: allLiveUser?.paginations?.inProgress || "0",
      },
      {
        label: "Today",
        value: allLiveUser?.paginations?.todayUserCount || "0",
      },
    ],
    [allLiveUser?.paginations],
  )

  // Filter state
  const [filter, setFilter] = useState({
    searchQuery: "",
    website: "all website",
    country: "all",
    isOnline: undefined,
    onlyTodayUser: undefined,
    from: undefined,
    to: undefined,
    status: "ALL",
  })

  // Add state for website options
  const [websites] = useState([
    "all website",
    "https://clickinpedia.com",
    "https://www.clickinpedia.com",
    "https://www.engineeringassignmenthelp.com",
    "https://www.casestudyhelp.uk",
    "https://portal.clickinpedia.io",
    "https://www.dissertationwritingservices.io",
    "https://www.managementassignmentwriter.com",
    "https://www.myobassignmenthelp.com",
    "https://www.xeroassignmenthelp.com",
    "https://www.assignmentexpert.io",
    "https://www.asignmenthelp.io",
    "https://www.marketingassignmenthelp.io",
    "https://www.onlinetafeassignmenthelp.com",
    "https://www.scienceassignmenthelp.com",
    "https://www.onlineexamhelp.io",
    "https://www.assignmentwriter.au",
    "https://www.financeassignmentwriter.com",
    "https://www.nursingassignments.io",
    "https://www.lawassignmentwriter.com",
    "https://www.assignmenthelpuk.io",
    "https://www.dissertationwriter.uk",
    "https://www.assessmenthelp.uk",
    "https://www.nursingassignments.uk",
    "https://www.accountingassignmenthelp.uk",
    "https://www.programmingassignmenthelp.uk",
    "https://www.examhelp.uk",
    "https://www.courseworkhelp.uk",
    "https://www.engineeringassignmenthelp.uk",
    "https://www.clickinpedia.io",
    "https://www.academicassignmenthelp.com",
    "https://helpwithassignments.io",
  ])

  const handleReply = useCallback((msg) => {
    setReplyingTo(msg)
    // Focus on the input field
    document.querySelector(".chat-input")?.focus()
  }, [])

  const cancelReply = useCallback(() => {
    setReplyingTo(null)
  }, [])

  const handleReplyClick = useCallback((replyToId) => {
    // Find the message ID from the replyTo object
    const messageId = replyToId
    if (messageId && messageRefs.current[messageId]) {
      // Set the highlighted message
      setHighlightedMessage(messageId)
      // Scroll to the message
      messageRefs.current[messageId]?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      })
    }
  }, [])

  const handleFilterClick = (stat) => {
    setActiveFilter(stat.label)

    // Update filter based on the selected stat
    let newStatus = "ALL"
    let newIsOnline = undefined
    let newOnlyTodayUser = undefined

    if (stat.label === "Online") {
      newStatus = "ISONLINE"
      newIsOnline = true
    } else if (stat.label === "Initiated") {
      newStatus = "INPROGRESS"
      newOnlyTodayUser = false
    } else if (stat.label === "Today") {
      newOnlyTodayUser = true
    }

    setFilter((prev) => ({
      ...prev,
      status: newStatus,
      isOnline: newIsOnline,
      onlyTodayUser: newOnlyTodayUser,
    }))

    // Reset pagination and fetch new data
    setPageData({ page: 1, limit: 17 })
    getAllLiveUser({
      page: 1,
      status: newStatus,
      tempIsOnline: newIsOnline,
      tempOnlyTodayUser: newOnlyTodayUser,
      resetPageData: true,
      firstTime: true,
    })
  }

  const handleEmojiClick = useCallback((emojiData) => {
    setMessage((prev) => prev + emojiData.emoji)
  }, [])

  // Update the handleFileUpload function to show loading state
  const handleFileUpload = useCallback(
    (e) => {
      if (e.target.files.length > 0) {
        setIsUploading(true) // Set uploading state to true

        const imageCallBack = (res) => {
          // Update attachments state with the uploaded files
          setAttachments(res?.imageUrls || [])
          setIsUploading(false) // Set uploading state to false when complete
        }

        const formData = new FormData()
        for (let i = 0; i < e.target.files.length; i++) {
          formData.append("images", e.target.files[i]) // Append each file to the FormData object
        }

        dispatch(uploadImageAction({ data: formData, cb: imageCallBack }))
        e.target.value = null
      }
    },
    [dispatch],
  )

  // Add a function to toggle the filter panel visibility after the other handler functions
  const toggleFilterPanel = () => {
    setShowFilterPanel((prev) => !prev)
  }

  // Add a function to toggle the filter modal
  const toggleFilterModal = () => {
    setShowFilterModal(!showFilterModal)
  }

  // Add a function to toggle the detailed view
  const toggleDetailedView = () => {
    setShowDetailedView(!showDetailedView)
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showEmojiPicker && !event.target.closest(".emoji-picker-container")) {
        setShowEmojiPicker(false)
      }
      if (showOptions && !event.target.closest(".options-container")) {
        setShowOptions(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [showEmojiPicker, showOptions])

  const getChatListOfUser = ({ userId }) => {
    setUnreadMessage((prev) => [...prev, userId])
    dispatch(getOpenChatListAction({ userType: user?.userType, userId }))
    setActiveVisitor(allLiveUser?.data?.findIndex((visitor) => visitor._id === userId) || 0)
    if (isMobile) {
      setShowChat(true)
    }
  }

  useEffect(() => {
    socket.on("new_open_chat_coming", ({ userId }) => {
      playNotificationSound()
      if (userId === openChatList?.client?._id) {
        getChatListOfUser({ userId })
      } else {
        getAllLiveUser({
          firstTime: true,
          page: 1,
          limit: pageData?.limit * pageData?.page,
        })
      }
    })
    return () => {
      socket.off("new_open_chat_coming")
    }
  }, [openChatList, user])

  useEffect(() => {
    socket.on("internal_chatlist_refresh", ({ userId }) => {
      playNotificationSound()
      if (userId === openChatList?.client?._id) {
        getChatListOfUser({ userId })
      } else {
        getAllLiveUser({
          firstTime: true,
          page: 1,
          limit: pageData?.limit * pageData?.page,
        })
      }
    })
    return () => {
      socket.off("internal_chatlist_refresh")
    }
  }, [openChatList, user])

  // Add a function to handle country filter change
  const handleCountryChange = (country) => {
    setSelectedCountry(country)
    setFilter((prev) => ({
      ...prev,
      country,
    }))

    // Reset pagination and fetch new data
    setPageData({ page: 1, limit: 17 })
    getAllLiveUser({
      page: 1,
      country,
      resetPageData: true,
      firstTime: true,
    })
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getAllLiveUser = ({
    page = pageData?.page,
    limit = pageData?.limit,
    tempWebsite = filter.website,
    // tempIsOnline = filter?.isOnline || filter.status==="ISONLINE",
    tempSearchQuery = filter.searchQuery,
    tempOnlyTodayUser = filter.onlyTodayUser,
    from = filter.from,
    to = filter.to,
    country = filter.country,
    firstTime = false,
    status = filter.status,
    resetPageData = "",
  }) => {
    if (resetPageData) {
      setPageData({ page: 1 })
    }
    setUnreadMessage([])
    const tempFilter = {
      website: tempWebsite !== "all website" ? tempWebsite : undefined,
      status: status,
      searchQuery: tempSearchQuery,
      limit,
      onlyTodayUser: tempOnlyTodayUser,
      from,
      to,
      country: country !== "all" ? country : undefined,
      page,
    }
    dispatch(
      getAllLiveUserListAction({
        hideError:true,
        firstTime,
        userType: user?.userType?.toLowerCase(),
        filter: tempFilter,
      }),
    )

  }

  // Handle website filter change
  const handleWebsiteChange = (website) => {
    setFilter((prev) => ({
      ...prev,
      website,
    }))

    // Reset pagination and fetch new data
    setPageData({ page: 1, limit: 17 })
    getAllLiveUser({
      page: 1,
      tempWebsite: website,
      resetPageData: true,
      firstTime: true,
    })
  }

  // Handle date range change
  const handleDateRangeChange = (type, value) => {
    const newDateRange = { ...dateRange, [type]: value }
    setDateRange(newDateRange)

    if (type === "to" && dateRange.from && value) {
      // Only apply filter when both dates are set and "to" date is changed
      setFilter((prev) => ({
        ...prev,
        from: new Date(dateRange.from).toISOString(),
        to: new Date(value).toISOString(),
      }))

      // Reset pagination and fetch new data
      setPageData({ page: 1, limit: 17 })
      getAllLiveUser({
        page: 1,
        from: new Date(dateRange.from).toISOString(),
        to: new Date(value).toISOString(),
        resetPageData: true,
        firstTime: true,
      })
    }
  }

  // Clear date filter
  const clearDateFilter = () => {
    setDateRange({ from: "", to: "" })
    setFilter((prev) => ({
      ...prev,
      from: undefined,
      to: undefined,
    }))

    // Reset pagination and fetch new data
    setPageData({ page: 1, limit: 17 })
    getAllLiveUser({
      page: 1,
      from: undefined,
      to: undefined,
      resetPageData: true,
      firstTime: true,
    })
  }

  // Add a function to apply all filters at once
  const applyFilters = () => {
    // Reset pagination and fetch new data with all current filters
    setPageData({ page: 1, limit: 17 })
    getAllLiveUser({
      page: 1,
      tempWebsite: filter.website,
      from: filter.from,
      to: filter.to,
      country: filter.country,
      resetPageData: true,
      firstTime: true,
    })

    // Close the modal
    setShowFilterModal(false)
  }

  // Add a function to reset all filters
  const resetAllFilters = () => {
    setDateRange({ from: "", to: "" })
    setSelectedCountry("all")
    setFilter({
      searchQuery: "",
      website: "all website",
      country: "all",
      isOnline: undefined,
      onlyTodayUser: undefined,
      from: undefined,
      to: undefined,
      status: filter.status, // Keep the current status filter
    })

    // Reset pagination and fetch new data
    setPageData({ page: 1, limit: 17 })
    getAllLiveUser({
      page: 1,
      tempWebsite: "all website",
      from: undefined,
      to: undefined,
      country: undefined,
      resetPageData: true,
      firstTime: true,
    })

    // Close the modal
    setShowFilterModal(false)
  }

  useEffect(() => {
    socket.on("all_live_user", () => {
      getAllLiveUser({
        firstTime: true,
        page: 1,
        limit: pageData?.limit * pageData?.page,
      })
    })
    return () => {
      socket.off("all_live_user")
    }
  }, [pageData,user])

  const playNotificationSound = () => {
    if (audioRef.current) {
      audioRef.current.play().catch((error) => console.error("Error playing sound:", error))
    }
  }

  // Initial data fetch
  useEffect(() => {
    if (user?.userType) {
      getAllLiveUser({ firstTime: true })
    }
  }, [user])

  // Update messages when openUserChatList changes
  useEffect(() => {
    setMessages({
      chatList: openUserChatList?.chatList,
      data: openUserChatList?.data,
    })
  }, [openUserChatList])

  // Add a function to check if a URL is an image
  const isImageUrl = useCallback((url) => {
    if (!url) return false
    return /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(url)
  }, [])

  // Fixed renderMessageContent function to properly handle image and document attachments
  const renderMessageContent = useCallback(
    (msg) => {
      // Check if the message has content or attachments
      if (!msg.content && !msg.message && (!msg.attachments || msg.attachments.length === 0)) {
        return <p className="text-sm leading-snug font-inter">No content</p>
      }

      // Handle messages with attachments array (from the message object in your prompt)
      if (msg.attachments && msg.attachments.length > 0) {
        return (
          <div className="mt-1">
            {msg.attachments.map((url, index) => {
              if (isImageUrl(url)) {
                return (
                  <div key={index} className="mb-2">
                    <img
                      src={url || "/placeholder.svg"}
                      alt={`Attachment ${index + 1}`}
                      className="rounded-md max-h-60 w-auto object-contain border border-gray-200"
                      // onError={(e) => {
                      //   e.target.onerror = null;
                      //   e.target.src = "/placeholder.svg";
                      // }}
                    />
                  </div>
                )
              } else if (url) {
                // For non-image files
                return (
                  <div key={index} className="mt-1 flex items-center mb-2">
                    <div className="p-2 bg-gray-100 rounded-md mr-2">
                      <FileText className="w-6 h-6 text-gray-500" />
                    </div>
                    <div>
                      <div className="text-sm font-medium">Document</div>
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`text-xs mt-1 inline-block px-2 py-0.5 rounded ${
                          msg.sender?.userType != "CLIENT" ? "bg-[#00204A] text-white" : "bg-white text-[#00204A]"
                        }`}
                      >
                        <span className="flex items-center">
                          <Download className="w-3 h-3 mr-1" /> Download
                        </span>
                      </a>
                    </div>
                  </div>
                )
              }
            })}
            {msg.content && <p className="text-sm leading-snug font-inter mt-2">{msg.content}</p>}
          </div>
        )
      }

      return <p className="text-sm leading-snug font-inter">{msg.content || msg.message}</p>
    },
    [isImageUrl, user?._id],
  )

  const renderRepliedMessage = useCallback(
    (repliedMessage) => {
      if (!repliedMessage) return null

      return (
        <div
          className="px-2 py-1 bg-gray-100 rounded-md mb-1 text-xs border-l-2 border-gray-400 mt-1 cursor-pointer hover:bg-gray-200 transition-colors"
          onClick={(e) => {
            e.stopPropagation()
            handleReplyClick(repliedMessage._id)
          }}
        >
          <div className="font-semibold text-xs text-gray-600">
            {repliedMessage.sender?._id === user?._id ? "You" : repliedMessage.sender?.name || repliedMessage.visitorId}
          </div>
          <div className="text-gray-600 truncate">
            {truncateString(repliedMessage.content || repliedMessage.message, 50)}
          </div>
        </div>
      )
    },
    [user, handleReplyClick],
  )

  // Platform icon renderer - memoized
  const renderPlatformIcon = useCallback((platform) => {
    if (!platform) return null

    const platformStr = String(platform).toUpperCase()

    if (platformStr.includes("ANDROID APP")) {
      return <Android className="w-4 h-4 text-green-600" />
    } else if (platformStr.includes("IOS APP") || platformStr.includes("MAC") || platformStr.includes("OS X")) {
      return <Apple className="w-4 h-4 text-gray-800" />
    } else if (platformStr.includes("WEBSITE") || platformStr.includes("WINDOWS")) {
      return <Globe className="w-4 h-4 text-blue-600" />
    } else {
      return <Globe className="w-4 h-4 text-gray-600" />
    }
  }, [])

  const visitors = allLiveUser?.data

  const handleSendMessageWithAttachments = useCallback(() => {
    if ((message?.trim() || attachments.length > 0) && openChatList?.client?._id) {
      const obj = {
        userId: messages?.data?._id,
        clientId: openChatList?.client?._id,
        sender: user?._id,
        type: attachments.length > 0 ? "file" : "text",
        messageType: attachments.length > 0 ? "file" : "text",
        content: message.trim(),
        attachments: attachments,
        replyTo: replyingTo ? replyingTo._id : null,
      }

      dispatch(
        sendMessageOpenAction({
          userType: user?.userType,
          data: obj,
          cb: () => {
            getChatListOfUser({ userId: openChatList?.client?._id })
          },
        }),
      )
      setMessage("")
      setAttachments([])
      setReplyingTo(null)
    }
  }, [message, attachments, messages, openChatList, replyingTo, user, dispatch])

  const removeAttachment = useCallback((index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index))
  }, [])

  useEffect(() => {
    if (userId) {
      getChatListOfUser({ userId, cb: () => setShowChat(true) })
    }
  }, [userId])

  const location = useLocation()
  useEffect(() => {
    // Parse the query string
    const queryParams = new URLSearchParams(location.search)
    const visitorId = queryParams.get("visitorId")
    if (visitorId) {
      getChatListOfUser({ userId: visitorId })
    }
  }, [])

  return isContentOnly ? (
    <ChatArea
      audioRef={audioRef}
      showChat={showChat}
      isMobile={isMobile}
      stats={stats}
      activeFilter={activeFilter}
      handleFilterClick={handleFilterClick}
      searchQuery={searchQuery}
      setSearchQuery={setSearchQuery}
      parentRef={parentRef}
      rowVirtualizer={rowVirtualizer}
      allLiveUser={allLiveUser}
      activeVisitor={activeVisitor}
      unreadMessage={unreadMessage}
      getChatListOfUser={getChatListOfUser}
      formatMessageTime={formatMessageTime}
      visitors={visitors}
      truncateString={truncateString}
      countryFlags={countryFlags}
      openChatList={openChatList}
      setShowChat={setShowChat}
      renderPlatformIcon={renderPlatformIcon}
      setopenLiveChat={setopenLiveChat}
      showOptions={showOptions}
      setShowOptions={setShowOptions}
      chatContainerRef={chatContainerRef}
      handleChatScroll={handleChatScroll}
      messageRefs={messageRefs}
      highlightedMessage={highlightedMessage}
      messages={messages}
      renderRepliedMessage={renderRepliedMessage}
      renderMessageContent={renderMessageContent}
      handleReply={handleReply}
      showScrollButton={showScrollButton}
      scrollToBottom={scrollToBottom}
      replyingTo={replyingTo}
      user={user}
      cancelReply={cancelReply}
      attachments={attachments}
      isImageUrl={isImageUrl}
      removeAttachment={removeAttachment}
      isUploading={isUploading}
      message={message}
      setMessage={setMessage}
      handleFileUpload={handleFileUpload}
      showEmojiPicker={showEmojiPicker}
      setShowEmojiPicker={setShowEmojiPicker}
      handleEmojiClick={handleEmojiClick}
      handleSendMessageWithAttachments={handleSendMessageWithAttachments}
      // Filter props
      websites={websites}
      currentWebsite={filter.website}
      handleWebsiteChange={handleWebsiteChange}
      dateRange={dateRange}
      handleDateRangeChange={handleDateRangeChange}
      clearDateFilter={clearDateFilter}
      // New modal filter props
      showFilterModal={showFilterModal}
      toggleFilterModal={toggleFilterModal}
      selectedCountry={selectedCountry}
      handleCountryChange={handleCountryChange}
      applyFilters={applyFilters}
      resetAllFilters={resetAllFilters}
      showDetailedView={showDetailedView}
      toggleDetailedView={toggleDetailedView}
      getAllLiveUser={getAllLiveUser}
      setPageData={setPageData}
      filter={filter}
    />
  ) : (
    <DashboardLayout>
      <ChatArea
        audioRef={audioRef}
        showChat={showChat}
        isMobile={isMobile}
        stats={stats}
        activeFilter={activeFilter}
        handleFilterClick={handleFilterClick}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        parentRef={parentRef}
        rowVirtualizer={rowVirtualizer}
        allLiveUser={allLiveUser}
        activeVisitor={activeVisitor}
        unreadMessage={unreadMessage}
        getChatListOfUser={getChatListOfUser}
        formatMessageTime={formatMessageTime}
        visitors={visitors}
        truncateString={truncateString}
        countryFlags={countryFlags}
        setShowChat={setShowChat}
        renderPlatformIcon={renderPlatformIcon}
        setopenLiveChat={setopenLiveChat}
        showOptions={showOptions}
        setShowOptions={setShowOptions}
        chatContainerRef={chatContainerRef}
        handleChatScroll={handleChatScroll}
        messageRefs={messageRefs}
        highlightedMessage={highlightedMessage}
        messages={messages}
        renderRepliedMessage={renderRepliedMessage}
        renderMessageContent={renderMessageContent}
        handleReply={handleReply}
        showScrollButton={showScrollButton}
        scrollToBottom={scrollToBottom}
        replyingTo={replyingTo}
        cancelReply={cancelReply}
        attachments={attachments}
        isImageUrl={isImageUrl}
        removeAttachment={removeAttachment}
        isUploading={isUploading}
        message={message}
        setMessage={setMessage}
        handleFileUpload={handleFileUpload}
        showEmojiPicker={showEmojiPicker}
        setShowEmojiPicker={setShowEmojiPicker}
        handleEmojiClick={handleEmojiClick}
        handleSendMessageWithAttachments={handleSendMessageWithAttachments}
        // Filter props
        websites={websites}
        currentWebsite={filter.website}
        handleWebsiteChange={handleWebsiteChange}
        dateRange={dateRange}
        handleDateRangeChange={handleDateRangeChange}
        clearDateFilter={clearDateFilter}
        // New modal filter props
        showFilterModal={showFilterModal}
        toggleFilterModal={toggleFilterModal}
        selectedCountry={selectedCountry}
        handleCountryChange={handleCountryChange}
        applyFilters={applyFilters}
        resetAllFilters={resetAllFilters}
        showDetailedView={showDetailedView}
        toggleDetailedView={toggleDetailedView}
        getAllLiveUser={getAllLiveUser}
        setPageData={setPageData}
        filter={filter}
      />
    </DashboardLayout>
  )
}

export default LiveChatInternal
